import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { type TaskKey, USABILITY_TASKS, UsabilityRecorder } from "./recorder";

const STORAGE_KEY = "tcg.admin.usability";

/** `?usability=on` switches the recorder on for this browser, `?usability=off` switches it off. */
export function usabilityEnabled(search: string): boolean {
  const wanted = new URLSearchParams(search).get("usability");
  try {
    if (wanted === "on") {
      localStorage.setItem(STORAGE_KEY, "on");
    } else if (wanted === "off") {
      localStorage.removeItem(STORAGE_KEY);
    }
    return localStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    return false;
  }
}

/**
 * The observer's small panel of the owner usability test (T11-50) — only when switched on with
 * `?usability=on`. Collapsed it is one small dot, so it does not help or distract the person
 * testing; the observer starts / ends each of the 5 tasks and downloads the session file.
 */
export function UsabilityPanel() {
  const { search, pathname } = useLocation();
  const [enabled] = useState(() => usabilityEnabled(search));
  return enabled ? <Panel pathname={pathname} /> : null;
}

function Panel({ pathname }: { pathname: string }) {
  const { t, i18n } = useTranslation("help");
  const recorder = useRef<UsabilityRecorder | null>(null);
  recorder.current ??= new UsabilityRecorder({
    width: window.innerWidth,
    height: window.innerHeight,
    touch: window.matchMedia?.("(pointer: coarse)").matches ?? false,
    language: i18n.resolvedLanguage ?? "",
  });
  const panel = useRef<HTMLDivElement>(null);
  const path = useRef(pathname);
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState<TaskKey | null>(null);
  const [done, setDone] = useState(0);

  useEffect(() => {
    path.current = pathname;
    recorder.current?.page(pathname);
  }, [pathname]);

  useEffect(() => {
    const inPanel = (target: EventTarget | null) =>
      target instanceof Node && panel.current?.contains(target) === true;
    const onPointer = (event: PointerEvent) => {
      if (!inPanel(event.target)) {
        const helpTip =
          event.target instanceof Element &&
          event.target.closest('[data-slot="help-tip"]') !== null;
        recorder.current?.click(path.current, helpTip);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (!inPanel(event.target)) {
        recorder.current?.key(path.current);
      }
    };
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (
            node instanceof Element &&
            (node.matches('[role="alert"]') || node.querySelector('[role="alert"]'))
          ) {
            recorder.current?.problem();
          }
        }
      }
    });
    document.addEventListener("pointerdown", onPointer, true);
    document.addEventListener("keydown", onKey, true);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      document.removeEventListener("pointerdown", onPointer, true);
      document.removeEventListener("keydown", onKey, true);
      observer.disconnect();
    };
  }, []);

  const start = (task: TaskKey) => {
    recorder.current?.start(task, path.current);
    setRunning(task);
    setOpen(false);
  };
  const finish = (ok: boolean) => {
    recorder.current?.finish(ok);
    setRunning(null);
    setDone(recorder.current?.session.runs.length ?? 0);
  };
  const download = () => {
    const blob = new Blob([recorder.current?.toJson() ?? "{}"], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `usability-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const runningNo = USABILITY_TASKS.find((task) => task.key === running)?.no;

  return (
    <div
      ref={panel}
      data-slot="usability-panel"
      data-running={running ?? ""}
      className="fixed bottom-24 left-2 z-[90] flex flex-col items-start gap-2 text-sm"
    >
      {open ? (
        <div className="flex w-64 flex-col gap-2 rounded-md border bg-background p-3 shadow-lg">
          <p className="font-semibold">{t("usability.title")}</p>
          <div className="grid grid-cols-5 gap-1">
            {USABILITY_TASKS.map((task) => (
              <button
                key={task.key}
                type="button"
                className="min-h-touch rounded border font-bold"
                aria-label={t("usability.start", { n: task.no })}
                onClick={() => {
                  start(task.key);
                }}
              >
                {task.no}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="min-h-touch rounded border px-2"
            onClick={download}
            data-action="usability-download"
          >
            {t("usability.download", { count: done })}
          </button>
        </div>
      ) : null}
      <div className="flex gap-1">
        <button
          type="button"
          aria-label={t("usability.panel")}
          aria-expanded={open}
          className="size-touch rounded-full border bg-background font-bold opacity-60 shadow"
          onClick={() => {
            setOpen((o) => !o);
          }}
        >
          {runningNo ?? "·"}
        </button>
        {running ? (
          <>
            <button
              type="button"
              className="min-h-touch rounded-full border bg-background px-3 opacity-60 shadow"
              data-action="usability-done"
              onClick={() => {
                finish(true);
              }}
            >
              {t("usability.done")}
            </button>
            <button
              type="button"
              className="min-h-touch rounded-full border bg-background px-3 opacity-60 shadow"
              data-action="usability-gave-up"
              onClick={() => {
                finish(false);
              }}
            >
              {t("usability.gave_up")}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
